import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareButtons } from "@/components/ShareButtons";
import { CashoutStatement } from "@/components/CashoutStatement";
import { monthLabel } from "@/pages/SummaryPage";

export default function CashoutPrintPage() {
  const [search] = useSearchParams();
  const month = search.get("month") || "";
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(month);

  useEffect(() => {
    if (!valid || !can("export")) return;
    api.get("/reports/cashouts", { params: { month } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!can("export")) return <p className="p-10 text-sm" data-testid="cashout-print-no-permission">You don't have permission to print or share reports.</p>;
  if (!valid) return <p className="p-10 text-sm" data-testid="cashout-print-invalid">Pick a month from the Cashout report.</p>;
  if (!data) return <p className="p-10 text-sm text-slate-500" data-testid="cashout-print-loading">Preparing page…</p>;
  const shown = { ...data, month, items: data.month === month ? data.items : [], people: data.month === month ? data.people : [], total: data.month === month ? data.total : 0 };

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <div className="no-print sticky top-0 z-10 bg-slate-900 text-white">
        <div className="max-w-[1100px] mx-auto px-4 h-14 flex items-center gap-2">
          <Link to="/cashouts" data-testid="cashout-print-back" className="flex items-center gap-1.5 text-sm font-semibold hover:underline">
            <ArrowLeft size={15} /> <span className="hidden sm:inline">Cashout report</span><span className="sm:hidden">Back</span>
          </Link>
          <span className="ml-auto" />
          <ShareButtons text={`Cashout report — ${monthLabel(month)}`}
            makeUrl={async () => `${window.location.origin}/c/${(await api.post("/reports/cashouts/share", { month })).data.token}`} />
          <button data-testid="cashout-print-btn" onClick={() => window.print()}
            className="h-9 px-4 bg-yellow-300 text-slate-900 font-bold flex items-center gap-2 hover:bg-yellow-200 transition-colors">
            <Printer size={15} /> <span className="hidden sm:inline">Print / Save PDF</span>
          </button>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto my-6 print:my-0 bg-white p-6 sm:p-10 print:p-0 shadow-sm print:shadow-none overflow-x-auto" data-testid="cashout-print-page">
        <CashoutStatement data={shown} />
      </div>
    </div>
  );
}
